import { DefineModule, ProvideAs } from '@mrbilit/vue-context-provider';
import { SupportSessionStatus } from './support.type';
import { SupportWorkFlow } from './supportWorkFlow';
import {
    SessionWorkflowApiApiSessionWorkflowGetUserSessionsGet,
    SessionWorkflowApiApiSessionWorkflowStartNewSessionPost,
} from '@clients/SupportApi';
import {
    type AnalyticsStore,
    type PosthogHandler,
    type AuthStore,
    type BaseUrls,
    type DateUtil,
} from '#imports';

const key = Symbol('Support');

@DefineModule(key)
export class Support {
    constructor(
        @ProvideAs('dayConfig')
        private dayConfig: DateUtil,
        @ProvideAs('authStore')
        private authStore: AuthStore,
        @ProvideAs('analyticsStore')
        private analyticsStore: AnalyticsStore,
        @ProvideAs('posthog')
        private posthog: PosthogHandler,
        @ProvideAs('baseUrls')
        private baseUrls: BaseUrls
    ) {}

    sessions: SupportWorkFlow[] = [];
    private loading = false;

    get isLoading() {
        return (
            this.loading ||
            this.currentWorkFlow?.state === 'loading' ||
            this.currentWorkFlow?.state === 'reconnecting'
        );
    }

    get isDisconnected() {
        return (
            this.currentWorkFlow?.state === 'closed' ||
            this.currentWorkFlow?.state === 'reconnecting'
        );
    }

    async loadSessions() {
        const xPlayerId = this.authStore.uuid;
        if (xPlayerId) {
            const { data } =
                await SessionWorkflowApiApiSessionWorkflowGetUserSessionsGet({
                    xPlayerID: xPlayerId,
                });
            this.sessions =
                data.userSeassionResponseItems?.map(
                    session =>
                        new SupportWorkFlow(
                            session.sessionId ?? 0,
                            session.publicId ?? '',
                            xPlayerId,
                            this.baseUrls.support,
                            // WRONG DI pattern
                            this.authStore,
                            this.posthog,
                            this.analyticsStore,
                            session
                        )
                ) || [];
        } else {
            throw new Error('no player id is available');
        }
    }

    private async createNewSessionKey() {
        const { data } =
            await SessionWorkflowApiApiSessionWorkflowStartNewSessionPost();
        return {
            sessionId: data.sessionId,
            publicId: data.publicId ?? undefined,
        };
    }

    get currentWorkFlow(): SupportWorkFlow | undefined {
        return this.sessions.find(({ selected }) => selected);
    }

    async addNewSession() {
        this.loading = true;
        await this.createAndStartNewSession();
        this.loading = false;
    }

    private async createAndStartNewSession(
        sessionId?: number,
        publicId?: string
    ) {
        if (!sessionId || !publicId) {
            ({ sessionId, publicId } = await this.createNewSessionKey());
        }
        const xPlayerId = this.authStore.uuid;
        if (xPlayerId && sessionId && publicId) {
            this.sessions = [
                new SupportWorkFlow(
                    sessionId,
                    publicId,
                    xPlayerId,
                    this.baseUrls.support,
                    // WRONG DI pattern
                    this.authStore,
                    this.posthog,
                    this.analyticsStore,
                    {
                        startedAt: this.dayConfig.gregory().toString(),
                        status: SupportSessionStatus.PreRegister,
                    }
                ),
                ...this.sessions,
            ];
            await this.sessions
                .find(session => session.sessionId === sessionId)
                ?.start();
        } else {
            throw new Error('xPlayerId is not provided');
        }
    }

    async init(sessionId?: number, publicKey?: string) {
        this.loading = true;
        try {
            if (sessionId && publicKey) {
                await this.createAndStartNewSession(sessionId, publicKey);
            } else {
                await this.loadSessions();
                const havePending = this.sessions.find(
                    ({ status }) =>
                        status === SupportSessionStatus.Pending ||
                        status === SupportSessionStatus.UnderReview
                );
                if (!havePending) {
                    await this.createAndStartNewSession();
                }
            }
        } finally {
            this.loading = false;
        }
    }
}
export type ISupport = Support;

export { Support as moduleClass, key };
